import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { VaultBaseComponent } from '@app/shared/components/base/vault-base.component';
import { DialogComponent } from '@app/shared/components/dialog/dialog.component';
import { ColumnDefinition } from '@app/shared/interfaces/column-definition.interface';
import {
  BankManagerDto,
  BankManagerHttpService,
  BankManagerPosition,
  CreateBankManagerDto,
  FilteredDataResultDto,
  PaginationDto,
  UpdateBankManagerDto,
} from '@core/services/data/financial-institution/bank-manager/bank-manager-http.service';
import { Option } from '@interfaces/common/option';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from '@services/helpers/confirmation.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { UtilsService } from '@services/helpers/utils.service';
import { contactNotesAriaLabel, formatContactNotesTooltip } from '@shared/utils/contact-notes.util';
import { canEditGlobalContactNotes } from '@shared/utils/global-contact-notes-permission';
import { MenuItem } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { Observable, of } from 'rxjs';
import { catchError, debounceTime, finalize, map } from 'rxjs/operators';

@Component({
  selector: 'app-modal-bank-managers',
  templateUrl: './modal-bank-managers.component.html',
  styleUrls: ['./modal-bank-managers.component.scss'],
  standalone: false,
})
export class ModalBankManagersComponent extends VaultBaseComponent implements OnChanges {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;
  @ViewChild('addEditDialog') addEditDialogRef: DialogComponent | null = null;

  @Input() financialInstitutionId: string | null = null;
  @Input() financialInstitutionName: string = '';
  @Output() modalClosed = new EventEmitter<void>();

  bankManagers: BankManagerDto[] = [];
  override isLoading = false;
  isSubmitting = false;
  pagination: PaginationDto = {
    page: 1,
    pageSize: 10,
  };
  totalRecords = 0;
  columnDefinitions: ColumnDefinition[] = [];
  searchTerm = '';

  // Form para cadastro/edição
  bankManagerForm: FormGroup;
  isEditMode = false;
  editingManagerId: string | null = null;
  positionOptions: Option[] = [];
  selectedPosition: Option | null = null;
  emailExists = false;
  isCheckingEmail = false;
  private isClosingDialog = false;

  constructor(
    translateService: TranslateService,
    private bankManagerService: BankManagerHttpService,
    private utilsService: UtilsService,
    private toast: ToastHelperService,
    private confirmationService: ConfirmationService,
    private formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {
    super(translateService);
    this.bankManagerForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email], [this.emailExistsValidator()]],
      name: ['', [Validators.required]],
      position: ['', [Validators.required]],
      phone: [''],
      location: [''],
      notes: [''],
    });
    this.setupEmailValidation();
    this.initializePositionOptions();
    this.initializeColumns();
  }

  emailExistsValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!control.value || !this.financialInstitutionId) {
        this.emailExists = false;
        this.updateFieldsState();
        return of(null);
      }

      const email = control.value.trim();
      if (!email) {
        this.emailExists = false;
        this.updateFieldsState();
        return of(null);
      }

      // Valida formato de email primeiro
      const emailControl = this.formBuilder.control(email, [Validators.email]);
      if (emailControl.invalid) {
        this.emailExists = false;
        this.updateFieldsState();
        return of(null);
      }

      this.isCheckingEmail = true;
      this.cdr.markForCheck();

      return this.bankManagerService
        .checkEmailExists(
          email,
          this.financialInstitutionId,
          this.isEditMode ? this.editingManagerId : null
        )
        .pipe(
          debounceTime(500),
          map((exists: boolean | null) => {
            this.isCheckingEmail = false;
            if (exists === true) {
              this.emailExists = true;
              this.updateFieldsState();
              this.cdr.markForCheck();
              return { emailExists: true };
            }
            this.emailExists = false;
            this.updateFieldsState();
            this.cdr.markForCheck();
            return null;
          }),
          catchError(() => {
            this.isCheckingEmail = false;
            this.emailExists = false;
            this.updateFieldsState();
            this.cdr.markForCheck();
            return of(null);
          })
        );
    };
  }

  setupEmailValidation(): void {
    this.bankManagerForm.get('email')?.valueChanges.subscribe(() => {
      this.emailExists = false;
      this.updateFieldsState();
    });
  }

  updateFieldsState(): void {
    const nameControl = this.bankManagerForm.get('name');
    const positionControl = this.bankManagerForm.get('position');
    const phoneControl = this.bankManagerForm.get('phone');
    const landlinePhoneControl = this.bankManagerForm.get('landlinePhone');
    const locationControl = this.bankManagerForm.get('location');
    const notesControl = this.bankManagerForm.get('notes');

    if (this.emailExists) {
      nameControl?.disable();
      positionControl?.disable();
      phoneControl?.disable();
      landlinePhoneControl?.disable();
      locationControl?.disable();
      notesControl?.disable();
    } else {
      nameControl?.enable();
      positionControl?.enable();
      phoneControl?.enable();
      landlinePhoneControl?.enable();
      locationControl?.enable();
      notesControl?.enable();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['financialInstitutionId'] && changes['financialInstitutionId'].currentValue) {
      this.loadBankManagers();
    }
  }

  open(): void {
    if (this.dialogRef) {
      this.initializeColumns();
      this.dialogRef.open('', '', '', 'Cancelar', '', 'modal-bank-managers', false, false);
      if (this.financialInstitutionId) {
        this.loadBankManagers();
      }
    }
  }

  close(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
    this.modalClosed.emit();
  }

  initializePositionOptions(): void {
    this.positionOptions = Object.values(BankManagerPosition).map(position => ({
      label: this.getPositionLabel(position),
      value: position,
    }));
  }

  getPositionLabel(position: BankManagerPosition): string {
    const labels: { [key in BankManagerPosition]: string } = {
      [BankManagerPosition.Superintendente]: 'Superintendente',
      [BankManagerPosition.GeneralManager]: 'Gerente Geral',
      [BankManagerPosition.RelationshipManager]: 'Gerente de Relacionamento',
      [BankManagerPosition.BusinessManager]: 'Gerente de Negócios',
      [BankManagerPosition.CorporateManager]: 'Corporate / Middle / BBA',
      [BankManagerPosition.CommercialOfficer]: 'Officer Comercial',
      [BankManagerPosition.AssistantManager]: 'Gerente Assistente',
      [BankManagerPosition.ClientSpecialist]: 'Especialista de Clientes',
      [BankManagerPosition.CreditAnalyst]: 'Analista de Crédito',
      [BankManagerPosition.CreditDesk]: 'Mesa de Crédito',
      [BankManagerPosition.DerivativesDesk]: 'Mesa de Derivativos',
      [BankManagerPosition.Trader]: 'Trader',
      [BankManagerPosition.RiskAnalyst]: 'Analista de Risco',
      [BankManagerPosition.RiskManager]: 'Gerente de Risco',
      [BankManagerPosition.Other]: 'Outro',
    };
    return labels[position] || position;
  }

  /**
   * Se o usuário logado pode editar a observação **global** do gerente. Espelha
   * `BankManagerValidator.CanWriteGlobalNotes` no backend, que é quem de fato recusa (422).
   */
  private readonly canEditGlobalNotesSignal = canEditGlobalContactNotes();

  get canEditGlobalNotes(): boolean {
    return this.canEditGlobalNotesSignal();
  }

  /**
   * Texto da observação **global** para o tooltip (vazio = sem observação, mostra "-" na célula).
   * Aqui só existe a global: esta listagem é do gerente por instituição, sem empresa no contexto.
   * Truncamento e preservação de quebras de linha ficam no helper compartilhado.
   */
  notesTooltip(notes: string | null | undefined): string {
    return formatContactNotesTooltip(notes);
  }

  /** `aria-label` da célula de Observações (leitor de tela precisa do conteúdo completo). */
  notesAriaLabel(notes: string | null | undefined): string {
    return contactNotesAriaLabel(notes);
  }

  initializeColumns(): void {
    this.columnDefinitions = [
      new ColumnDefinition({ field: 'name', header: 'Nome' }),
      new ColumnDefinition({ field: 'email', header: 'Email' }),
      new ColumnDefinition({ field: 'phone', header: 'Telefone', template: 'phone' }),
      new ColumnDefinition({
        field: 'position',
        header: 'Cargo',
        template: 'position',
      }),
      // Texto livre: a célula mostra só uma linha truncada e o texto completo vai no tooltip
      // `long-text-tooltip` (hover ou foco), para uma observação longa não inflar a linha.
      // Esta listagem é do gerente por instituição, sem empresa no contexto: a única observação aqui
      // é a global. O cabeçalho diz isso por extenso — "Observações" sozinho deixaria o leitor
      // supondo que é a anotação da empresa dele.
      new ColumnDefinition({
        field: 'notes',
        header: 'Observações (todas as empresas)',
        template: 'notes',
        style: { 'max-width': '18rem' },
      }),
    ];
  }

  loadBankManagers(): void {
    if (!this.financialInstitutionId) return;

    this.isLoading = true;
    const pagination: PaginationDto = {
      ...this.pagination,
      filter: this.searchTerm || null,
    };

    this.bankManagerService
      .getAll(pagination, this.financialInstitutionId)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (response: FilteredDataResultDto<BankManagerDto> | null) => {
          if (response) {
            this.bankManagers = response.data;
            this.totalRecords = response.total;
            this.cdr.detectChanges();
          }
        },
        error: error => {
          console.error('Error loading bank managers:', error);
          this.toast.showToastError(
            'Erro ao carregar gerentes',
            error?.errors?.length ? error.errors[0].message : 'Erro ao carregar lista de gerentes'
          );
        },
      });
  }

  handleTablePagination(event: PaginatorState): void {
    this.pagination = {
      page: (event.page || 0) + 1,
      pageSize: event.rows || 10,
    };
    this.loadBankManagers();
  }

  handleSearch(search: string): void {
    this.searchTerm = search;
    this.pagination.page = 1;
    this.loadBankManagers();
  }

  openAddDialog(): void {
    this.isEditMode = false;
    this.editingManagerId = null;
    this.emailExists = false;
    this.bankManagerForm.reset();
    this.selectedPosition = null;
    this.updateFieldsState();
    if (this.addEditDialogRef) {
      this.addEditDialogRef.open(
        '',
        '',
        '',
        'Cancelar',
        '',
        'modal-add-edit-bank-manager',
        false,
        false
      );
    }
  }

  openEditDialog(manager: BankManagerDto): void {
    this.isEditMode = true;
    this.editingManagerId = manager.id;
    this.emailExists = false;
    this.bankManagerForm.patchValue({
      name: manager.name,
      email: manager.email,
      phone: manager.phone || '',
      position: manager.position || '',
      location: manager.location || '',
      notes: manager.notes || '',
    });
    // Atualiza o selectedPosition para o select
    if (manager.position) {
      this.selectedPosition =
        this.positionOptions.find(opt => opt.value === manager.position) || null;
    } else {
      this.selectedPosition = null;
    }
    this.updateFieldsState();
    if (this.addEditDialogRef) {
      this.addEditDialogRef.open(
        '',
        '',
        '',
        'Cancelar',
        '',
        'modal-add-edit-bank-manager',
        false,
        false
      );
    }
  }

  closeAddEditDialog(): void {
    // Evita chamadas duplicadas
    if (this.isClosingDialog) {
      return;
    }

    this.isClosingDialog = true;

    // Limpa o formulário e estados primeiro
    this.bankManagerForm.reset();
    this.selectedPosition = null;
    this.emailExists = false;
    this.isEditMode = false;
    this.editingManagerId = null;
    this.updateFieldsState();

    // Fecha a modal
    if (this.addEditDialogRef) {
      try {
        this.addEditDialogRef.close();
      } catch (error) {
        console.warn('Erro ao fechar modal:', error);
      }
    }

    // Força a detecção de mudanças
    this.cdr.detectChanges();

    // Reseta a flag após um pequeno delay
    setTimeout(() => {
      this.isClosingDialog = false;
    }, 500);
  }

  onPositionChange(option: Option | null): void {
    this.selectedPosition = option;
    this.bankManagerForm.patchValue({
      position: option?.value || '',
    });
  }

  submitBankManager(): void {
    if (this.bankManagerForm.invalid || !this.financialInstitutionId || this.emailExists) {
      this.bankManagerForm.markAllAsTouched();
      if (this.emailExists) {
        this.toast.showToastError('Email já existente', 'Por favor, use um email diferente.');
      }
      return;
    }

    this.isSubmitting = true;
    const formValue = this.bankManagerForm.getRawValue();

    if (this.isEditMode && this.editingManagerId) {
      const updateData: UpdateBankManagerDto = {
        id: this.editingManagerId,
        name: formValue.name,
        email: formValue.email,
        phone: formValue.phone || null,
        landlinePhone: formValue.landlinePhone || null,
        position: formValue.position || null,
        location: formValue.location || null,
        // String vazia (campo limpo pelo usuário) limpa as observações no backend.
        // Somente leitura para quem não é do time interno: `null` faz o backend preservar em vez de
        // recusar (422) por causa de um texto que o usuário nem podia editar.
        notes: this.canEditGlobalNotes ? (formValue.notes ?? '') : null,
        financialInstitutionId: this.financialInstitutionId,
      };

      this.bankManagerService
        .update(this.editingManagerId, updateData)
        .pipe(
          finalize(() => {
            this.isSubmitting = false;
            this.cdr.detectChanges();
          })
        )
        .subscribe({
          next: success => {
            // Se chegou aqui sem erro, considera sucesso mesmo se success for null/undefined
            this.toast.showToastSuccess('Gerente atualizado com sucesso!', '');

            // Fecha a modal e limpa o formulário
            this.closeAddEditDialog();

            // Aguarda um pouco para garantir que a animação de fechamento inicie antes de recarregar
            setTimeout(() => {
              this.loadBankManagers();
            }, 300);
          },
          error: error => {
            console.error('Error updating bank manager:', error);
            this.toast.showToastError(
              'Erro ao atualizar gerente',
              error?.errors?.length ? error.errors[0].message : 'Erro ao atualizar gerente bancário'
            );
          },
        });
    } else {
      if (!formValue.position) {
        this.toast.showToastError('Cargo é obrigatório', 'Por favor, selecione um cargo.');
        this.bankManagerForm.get('position')?.markAsTouched();
        return;
      }

      const createData: CreateBankManagerDto = {
        name: formValue.name,
        email: formValue.email,
        position: formValue.position as BankManagerPosition,
        phone: formValue.phone || null,
        landlinePhone: formValue.landlinePhone || null,
        location: formValue.location || null,
        notes: this.canEditGlobalNotes ? formValue.notes || null : null,
        financialInstitutionId: this.financialInstitutionId,
      };

      this.bankManagerService
        .create(createData)
        .pipe(
          finalize(() => {
            this.isSubmitting = false;
            this.cdr.detectChanges();
          })
        )
        .subscribe({
          next: success => {
            // Se chegou aqui sem erro, considera sucesso mesmo se success for null/undefined
            this.toast.showToastSuccess('Gerente cadastrado com sucesso!', '');

            // Fecha a modal e limpa o formulário
            this.closeAddEditDialog();

            // Aguarda um pouco para garantir que a animação de fechamento inicie antes de recarregar
            setTimeout(() => {
              this.loadBankManagers();
            }, 300);
          },
          error: error => {
            console.error('Error creating bank manager:', error);
            this.toast.showToastError(
              'Erro ao cadastrar gerente',
              error?.errors?.length ? error.errors[0].message : 'Erro ao cadastrar gerente bancário'
            );
          },
        });
    }
  }

  deleteBankManager(manager: BankManagerDto): void {
    this.confirmationService
      .confirm(
        'Remover gerente bancário?',
        `Tem certeza que deseja remover o gerente ${manager.name}?`
      )
      .subscribe(confirmed => {
        if (confirmed) {
          this.isLoading = true;
          this.bankManagerService
            .delete(manager.id)
            .pipe(
              finalize(() => {
                this.isLoading = false;
                this.cdr.detectChanges();
              })
            )
            .subscribe({
              next: success => {
                // Se chegou aqui sem erro, considera sucesso mesmo se success for null/undefined
                this.toast.showToastSuccess('Gerente removido com sucesso!', '');
                this.loadBankManagers();
              },
              error: error => {
                console.error('Error deleting bank manager:', error);
                this.toast.showToastError(
                  'Erro ao remover gerente',
                  error?.errors?.length
                    ? error.errors[0].message
                    : 'Erro ao remover gerente bancário'
                );
              },
            });
        }
      });
  }

  getMenuActions(item: BankManagerDto): MenuItem[] {
    return [
      {
        label: 'Editar',
        icon: 'pi pi-pencil',
        command: () => {
          this.openEditDialog(item);
        },
      },
      {
        label: 'Excluir',
        icon: 'pi pi-trash',
        command: () => {
          this.deleteBankManager(item);
        },
      },
    ];
  }

  isFormValid(): boolean {
    return this.bankManagerForm.valid && !this.emailExists;
  }
}
